"use client";

import Cookies from "js-cookie";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import {
  resolveThemeMode,
  readThemePreferenceFromCookie,
  systemPrefersDarkMode,
  type ResolvedThemeMode,
  type ThemePreference,
} from "./theme-preference";

const customThemes: Record<string, { primaryColor: string }> = {
  default: {
    primaryColor: "#0B6DA4",
  },
  kode: {
    primaryColor: "#234ea2",
  },
  ryze: {
    primaryColor: "#032F39",
  },
  owest: {
    primaryColor: "#1b2348",
  },
};

function getTenantTheme(nextSubdomain: string) {
  switch (nextSubdomain) {
    case "kode":
      return customThemes.kode;
    case "ryze":
      return customThemes.ryze;
    case "owest":
      return customThemes.owest;
    default:
      return customThemes.default;
  }
}

type ColorModeContextType = {
  /** Resolved appearance used by UI */
  mode: ResolvedThemeMode;
  /** User-selected preference, including system */
  preference: ThemePreference;
  primaryColor: string;
  setPreference: (preference: ThemePreference) => void;
  /** @deprecated Use setPreference */
  setMode: (preference: ThemePreference) => void;
  switchTheme: (page: string) => void;
  subdomain: string;
};

export const ColorModeContext = createContext<ColorModeContextType>(
  {} as ColorModeContextType
);

type ColorModeContextProviderProps = {
  defaultPreference?: ThemePreference;
  defaultResolvedMode?: ResolvedThemeMode;
  subdomain: string;
};

function resolveMode(preference: ThemePreference): ResolvedThemeMode {
  return resolveThemeMode(preference, systemPrefersDarkMode());
}

const THEME_COOKIE_OPTIONS = { expires: 365, path: "/", sameSite: "lax" as const };

function applyDocumentTheme(resolvedMode: ResolvedThemeMode) {
  if (typeof document === "undefined") {
    return;
  }

  document.documentElement.classList.toggle("dark", resolvedMode === "dark");
  document.documentElement.style.colorScheme =
    resolvedMode === "dark" ? "dark" : "light";
}

export const ColorModeContextProvider: React.FC<
  PropsWithChildren<ColorModeContextProviderProps>
> = ({
  children,
  defaultPreference = "system",
  defaultResolvedMode = "light",
  subdomain,
}) => {
  const [preference, setPreferenceState] =
    useState<ThemePreference>(defaultPreference);
  const [resolvedMode, setResolvedMode] = useState<ResolvedThemeMode>(
    defaultResolvedMode
  );
  const [hasHydrated, setHasHydrated] = useState(false);
  const [tenantTheme, setTenantTheme] = useState(() =>
    getTenantTheme(subdomain)
  );
  const hasSyncedFromCookie = useRef(false);

  // Keep SSR and the first client render identical; sync real theme after hydration.
  const mode = hasHydrated ? resolvedMode : defaultResolvedMode;

  useEffect(() => {
    if (!hasSyncedFromCookie.current) {
      hasSyncedFromCookie.current = true;
      const cookiePreference = readThemePreferenceFromCookie(defaultPreference);
      if (cookiePreference !== preference) {
        setPreferenceState(cookiePreference);
        return;
      }
    }

    const nextResolvedMode = resolveMode(preference);
    setResolvedMode(nextResolvedMode);
    applyDocumentTheme(nextResolvedMode);
    Cookies.set("theme", preference, THEME_COOKIE_OPTIONS);
    setHasHydrated(true);

    if (preference !== "system") {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      const systemResolvedMode = resolveMode("system");
      setResolvedMode(systemResolvedMode);
      applyDocumentTheme(systemResolvedMode);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [preference, defaultPreference]);

  const setPreference = useCallback((nextPreference: ThemePreference) => {
    setPreferenceState(nextPreference);
  }, []);

  const switchTheme = useCallback((nextSubdomain: string) => {
    setTenantTheme(getTenantTheme(nextSubdomain));
  }, []);

  useEffect(() => {
    switchTheme(subdomain);
  }, [subdomain, switchTheme]);

  const contextValue = useMemo(
    () => ({
      mode,
      preference,
      setPreference,
      setMode: setPreference,
      switchTheme,
      primaryColor: tenantTheme.primaryColor,
      subdomain,
    }),
    [
      mode,
      preference,
      setPreference,
      subdomain,
      switchTheme,
      tenantTheme.primaryColor,
    ]
  );

  return (
    <ColorModeContext.Provider value={contextValue}>
      {children}
    </ColorModeContext.Provider>
  );
};

export type { ResolvedThemeMode, ThemePreference } from "./theme-preference";
